import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  AdvancedFilterButton,
  AdvancedFilterPanel,
  FilterPanelCollapse,
} from '@/components/common/AdvancedFilter';
import { BackButton } from '@/components/common/BackButton';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
import { StatCardGrid, type LabeledStat } from '@/components/common/StatCardGrid';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { Button } from '@/components/ui/button';
import { TAKA_SYMBOL } from '@/constants/app.constants';
import { useHistoryBack } from '@/hooks/useHistoryBack';
import { useRevealLastViewedRow } from '@/hooks/useLastViewedRow';
import { useLocalListSearch } from '@/hooks/useLocalListSearch';
import { getFilterPanelProps } from '@/hooks/useUrlSyncedFilters';
import { getDateFromToFilterFields } from '@/utils/filterFields';
import { getTableEmptyText } from '@/utils/listSearch';
import {
  donationCandidatesQueryOptions,
  donationProjectsQueryOptions,
} from '../api/marketPromotionP2.api';
import {
  getDonationCandidateColumns,
  PICK_COLUMN_ID,
} from '../components/DonationCandidateColumns';
import { PublishDonationBatchDialog } from '../components/PublishDonationBatchDialog';
import { useDonationCandidateFilters } from '../hooks/useDonationCandidateFilters';
import { useDonationCandidatePicks } from '../hooks/useDonationCandidatePicks';
import { usePublishDonationBatch } from '../hooks/useMpP2Mutations';
import type { DonationCandidate } from '../types/marketPromotionP2.types';
import { getDonationProjectFilterField } from '../utils/mpFilterFields';

const PUBLISH_PANEL_PATH = '/market-promotion/publish-panel';
const SEARCH_PLACEHOLDER = 'ZM name, HR ID or mobile';
const FIT_CONTENT_COLUMN_IDS = [PICK_COLUMN_ID] as const;

const toSearchableValues = (candidate: DonationCandidate) => [
  candidate.fullName,
  candidate.hrEmpId,
  candidate.mobile,
];

const sumDonationAmount = (candidates: DonationCandidate[]): number =>
  candidates.reduce((total, candidate) => total + candidate.donationAmount, 0);

export function NewDonationBatchPage() {
  const navigate = useNavigate();
  const goBack = useHistoryBack(() => void navigate({ to: PUBLISH_PANEL_PATH }));
  const controller = useDonationCandidateFilters();
  const { params } = controller;
  const hasProject = params.promoItemId !== undefined;

  const [isFilterOpen, setIsFilterOpen] = useState(!hasProject);
  const [isPublishOpen, setIsPublishOpen] = useState(false);

  const projectsQuery = useQuery(donationProjectsQueryOptions());
  const candidatesQuery = useQuery(donationCandidatesQueryOptions(params));
  const candidates = useMemo(() => candidatesQuery.data ?? [], [candidatesQuery.data]);

  const { searchText, setSearchText, visibleRows } = useLocalListSearch({
    rows: candidates,
    appliedText: controller.appliedSearchText,
    onApply: controller.applySearchText,
    toSearchableValues,
  });

  const picks = useDonationCandidatePicks(candidates, visibleRows, params);
  const pickedCount = picks.pickedCandidates.length;

  const publish = usePublishDonationBatch(() => {
    setIsPublishOpen(false);
    void navigate({ to: PUBLISH_PANEL_PATH });
  });

  function handlePublish(batchName: string | undefined) {
    if (params.promoItemId === undefined) return;
    publish.mutate({
      userIds: picks.pickedCandidates.map((candidate) => candidate.userId),
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
      promoItemId: params.promoItemId,
      amount: picks.pickedAmount,
      ...(batchName === undefined ? {} : { comment: batchName }),
    });
  }

  const isCandidateListReady = candidatesQuery.data !== undefined;
  const stats: LabeledStat[] = [
    {
      key: 'zonalManagers',
      label: 'ZMs',
      value: isCandidateListReady ? candidates.length : undefined,
    },
    {
      key: 'donationAmount',
      label: 'Donation Amount',
      value: isCandidateListReady ? Math.round(sumDonationAmount(candidates)) : undefined,
      unit: TAKA_SYMBOL,
    },
    {
      key: 'pickedZonalManagers',
      label: 'Picked ZMs',
      value: pickedCount,
      isMuted: pickedCount === 0,
    },
    {
      key: 'pickedAmount',
      label: 'Picked Amount',
      value: Math.round(picks.pickedAmount),
      unit: TAKA_SYMBOL,
      isMuted: pickedCount === 0,
    },
  ];

  const lastViewedZonalManagerId = useRevealLastViewedRow(
    'donationCandidates',
    params.promoItemId ?? null,
  );

  const columns = getDonationCandidateColumns(
    {
      isPicked: picks.isPicked,
      onTogglePick: picks.togglePick,
      isEveryVisiblePicked: picks.isEveryVisiblePicked,
      isSomeVisiblePicked: picks.isSomeVisiblePicked,
      onToggleAllVisible: picks.toggleAllVisible,
    },
    controller.appliedSearchText,
    (candidate) => (
      <Link
        to="/market-promotion/publish-panel/zonal-managers/$userId"
        params={{ userId: candidate.userId }}
        search={{
          promoItemId: params.promoItemId ?? 0,
          dateFrom: params.dateFrom,
          dateTo: params.dateTo,
        }}
      />
    ),
  );

  function renderCandidates() {
    if (!hasProject) {
      return (
        <EmptyState
          kind="pickFirst"
          title="Pick a donation project"
          description="Choose a Donation Project in Filters to see the ZMs waiting for a batch."
          fill
        />
      );
    }
    if (candidatesQuery.error) {
      return (
        <ErrorState
          error={candidatesQuery.error}
          fallbackTitle="Could not load the ZMs waiting for a batch"
          onRetry={() => void candidatesQuery.refetch()}
          fill
        />
      );
    }
    return (
      <>
        <StatCardGrid stats={stats} isLoading={candidatesQuery.isLoading} />
        <DataTable
          data={visibleRows}
          columns={columns}
          isLoading={candidatesQuery.isLoading}
          className="lg:flex-1"
          stickyColumnId="zonalManager"
          fitContentColumnIds={FIT_CONTENT_COLUMN_IDS}
          isRowHighlighted={(candidate) => candidate.userId === lastViewedZonalManagerId}
          isRowPicked={picks.isPicked}
          {...getTableEmptyText(
            controller.appliedSearchText,
            'No donation is waiting for a batch in these dates',
          )}
        />
      </>
    );
  }

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="New Batch"
        leading={<BackButton onClick={goBack} />}
        titleAddon={
          <DateRangeBadge
            onClick={() => setIsFilterOpen(true)}
            dateFrom={params.dateFrom}
            dateTo={params.dateTo}
          />
        }
        search={{ value: searchText, onChange: setSearchText, placeholder: SEARCH_PLACEHOLDER }}
        actions={
          <>
            <AdvancedFilterButton
              isOpen={isFilterOpen}
              activeCount={controller.activeFilterCount}
              onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
            />
            <Button disabled={pickedCount === 0} onClick={() => setIsPublishOpen(true)}>
              Publish
            </Button>
          </>
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <AdvancedFilterPanel
          fields={[
            getDonationProjectFilterField(projectsQuery.data, projectsQuery.isLoading, {
              isRequired: true,
            }),
            ...getDateFromToFilterFields(),
          ]}
          {...getFilterPanelProps(controller)}
        />
      </FilterPanelCollapse>

      {renderCandidates()}

      <PublishDonationBatchDialog
        open={isPublishOpen}
        onOpenChange={setIsPublishOpen}
        pickedZonalManagerCount={pickedCount}
        pickedAmount={picks.pickedAmount}
        isPending={publish.isPending}
        onPublish={handlePublish}
      />
    </div>
  );
}
